import React, { useMemo, useState } from "react";
import "./external-templates-page.scss";
import "../../components/TemplateTable.css";
import { useTemplates, useTemplateForm } from "../../hooks";
import { TemplateTable, TemplateViewModal, TemplateFormModal, TemplateAnalyticsModal, Pagination } from "../../components";
import Button from "../../../../shared/components/dashboard-layout/components/button/buttonComponent";
import Table from "../../../../shared/components/dashboard-layout/components/table/tableComponent";
import TemplatesApi from "../../../../api/templates-api";

const ExternalTemplatesPage = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [refreshingApprovalStatus, setRefreshingApprovalStatus] = useState({});
  const [analyticsTemplate, setAnalyticsTemplate] = useState(null);
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = useState(false);

  // Custom hooks
  const {
    templates,
    loading,
    currentPage,
    totalPages,
    handlePageChange,
    createTemplate,
    updateTemplate,
    updateTemplateInList
  } = useTemplates();

  const {
    templateForm,
    validationErrors,
    formSubmitting,
    setFormSubmitting,
    validateForm,
    handleFormChange,
    handleVariableChange,
    addVariable,
    removeVariable,
    addCtaButton,
    removeCtaButton,
    handleCtaButtonChange,
    resetForm,
    loadTemplateData,
    getFormData
  } = useTemplateForm();

  // Modal handlers
  const handleViewTemplate = (template) => {
    setSelectedTemplate(template);
    setIsViewModalOpen(true);
  };

  const handleOpenCreateModal = () => {
    resetForm();
    setIsCreateModalOpen(true);
  };

  const handleOpenEditModal = (template) => {
    loadTemplateData(template);
    setEditingTemplateId(template._id);
    setIsEditModalOpen(true);
  };

  const handleOpenAnalyticsModal = (template) => {
    setAnalyticsTemplate(template);
    setIsAnalyticsModalOpen(true);
  };

  const handleCloseModals = () => {
    setIsViewModalOpen(false);
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setIsAnalyticsModalOpen(false);
    setSelectedTemplate(null);
    setEditingTemplateId(null);
    setAnalyticsTemplate(null);
  };

  // Form submission handlers
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setFormSubmitting(true);
    const result = await createTemplate(getFormData());
    
    if (result.success) {
      handleCloseModals();
    }
    
    setFormSubmitting(false);
  };

  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setFormSubmitting(true);
    const result = await updateTemplate(editingTemplateId, getFormData());
    
    if (result.success) {
      handleCloseModals();
    }
    
    setFormSubmitting(false);
  };

  const handleDeleteTemplate = (template) => {
    // TODO: Implement delete functionality
    console.log("Delete template:", template);
  };

  const handleTemplateUpdate = (updatedTemplate) => {
    // Update the template in the list
    updateTemplateInList(updatedTemplate);
    
    // If this is the currently selected template, update it as well
    if (selectedTemplate && selectedTemplate._id === updatedTemplate._id) {
      setSelectedTemplate(updatedTemplate);
    }
  };

  const handleRefreshApprovalStatus = async (templateId) => {
    setRefreshingApprovalStatus((prev) => ({ ...prev, [templateId]: true }));
    try {
      const response = await TemplatesApi.private.checkTwilioApprovalStatus(templateId);
      const updatedTemplate = response?.data?.data;
      if (updatedTemplate?._id) {
        handleTemplateUpdate(updatedTemplate);
      }
    } catch (error) {
      console.error("Error refreshing approval status:", error);
    } finally {
      setRefreshingApprovalStatus((prev) => ({ ...prev, [templateId]: false }));
    }
  };

  const templateColumns = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        cell: (template) => template.name,
      },
      {
        key: "platform",
        header: "Platform",
        cell: (template) => template.templatePlatform,
      },
      {
        key: "content",
        header: "Content",
        cell: (template) =>
          template.value
            ? template.value.substring(0, 50) + (template.value.length > 50 ? "..." : "")
            : "No message",
      },
      {
        key: "approvalStatus",
        header: "Approval Status",
        cell: (template) => {
          const approvalStatus = template?.metadata?.approvalStatus || "pending";
          const badgeClass =
            approvalStatus === "approved"
              ? "bg-success"
              : approvalStatus === "rejected"
              ? "bg-danger"
              : "bg-warning text-dark";

          return (
            <div className="d-flex align-items-center gap-2">
              <span className={`badge ${badgeClass}`}>{approvalStatus}</span>
              <button
                className="bouncy-button white small"
                onClick={() => handleRefreshApprovalStatus(template._id)}
                type="button"
                disabled={!!refreshingApprovalStatus[template._id]}
                title="Refresh approval status"
              >
                <i
                  className={`${
                    refreshingApprovalStatus[template._id] ? "mdi mdi-loading mdi-spin" : "mdi mdi-refresh"
                  } text-black`}
                ></i>
              </button>
            </div>
          );
        },
      },
      {
        key: "actions",
        header: "Actions",
        cell: (template) => (
          <>
            <button
              className="bouncy-button white small me-1"
              onClick={() => handleViewTemplate(template)}
              type="button"
              title="View"
            >
              <i className="text-black mdi mdi-eye"></i>
            </button>
            <button
              className="bouncy-button white small me-1"
              onClick={() => handleOpenAnalyticsModal(template)}
              type="button"
              title="Analytics"
            >
              <i className="text-black mdi mdi-chart-bar"></i>
            </button>
            <button
              className="bouncy-button white small me-1"
              onClick={() => handleOpenEditModal(template)}
              type="button"
              title="Edit"
            >
              <i className="text-black mdi mdi-pencil"></i>
            </button>
            <button
              className="bouncy-button negative small me-1"
              onClick={() => handleDeleteTemplate(template)}
              type="button"
              title="Delete"
            >
              <i className="text-black mdi mdi-delete"></i>
            </button>
          </>
        ),
      },
    ],
    [handleDeleteTemplate, handleOpenEditModal, handleOpenAnalyticsModal, handleRefreshApprovalStatus, handleViewTemplate, refreshingApprovalStatus]
  );

  return (
    <div className="external-templates-page">
      <div className="pageHeaderTitle">
        <div className="pageTitle">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M8 12.2H15M8 16.2H12.38M10 3.67676V2.01221M14 3.67676V2.01221M7.2 7.70003H16.8C17.9201 7.70003 18.4802 7.70003 18.908 7.91639C19.2843 8.10807 19.5903 8.41421 19.782 8.79128C20 9.21692 20 9.77919 20 10.9037V16.8C20 17.9246 20 18.4868 19.782 18.9124C19.5903 19.2895 19.2843 19.5956 18.908 19.7873C18.4802 20.0037 17.9201 20.0037 16.8 20.0037H7.2C6.0799 20.0037 5.51984 20.0037 5.09202 19.7873C4.71569 19.5956 4.40973 19.2895 4.21799 18.9124C4 18.4868 4 17.9246 4 16.8V10.9037C4 9.77919 4 9.21692 4.21799 8.79128C4.40973 8.41421 4.71569 8.10807 5.09202 7.91639C5.51984 7.70003 6.07989 7.70003 7.2 7.70003Z" stroke="var(--text-primary)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
          External Templates
        </div>

        <div className="right">
          <Button className={"small"} onClick={handleOpenCreateModal}><i className="fas fa-plus"></i> <span>Create Template</span></Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : templates?.length === 0 ? (
        <div className="text-center">
          <p>No templates found</p>
        </div>
      ) : (
        <Table
          columns={templateColumns}
          data={templates}
          rowKey="_id"
        />
      )}

      {!loading && templates?.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {/* <div className="row">
        <div className="col-12">

          <div className="tableWrapper mt-3">
              {loading ? (
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : templates?.length === 0 ? (
                <div className="text-center">
                  <p>No templates found</p>
                </div>
              ) : (
                <TemplateTable
                  templates={templates}
                  onView={handleViewTemplate}
                  onEdit={handleOpenEditModal}
                  onDelete={handleDeleteTemplate}
                />
              )}

              {!loading && templates?.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
          </div>
            
        </div>
      </div> */}

      {/* View Template Modal */}
      <TemplateViewModal
        template={selectedTemplate}
        isOpen={isViewModalOpen}
        onClose={handleCloseModals}
        onTemplateUpdate={handleTemplateUpdate}
      />

      {/* Create Template Modal */}
      <TemplateFormModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModals}
        onSubmit={handleCreateTemplate}
        title="Create New Template"
        formData={templateForm}
        validationErrors={validationErrors}
        formSubmitting={formSubmitting}
        onFormChange={handleFormChange}
        onVariableChange={handleVariableChange}
        onAddVariable={addVariable}
        onRemoveVariable={removeVariable}
        onAddCtaButton={addCtaButton}
        onRemoveCtaButton={removeCtaButton}
        onCtaButtonChange={handleCtaButtonChange}
      />

      {/* Analytics Modal */}
      <TemplateAnalyticsModal
        template={analyticsTemplate}
        isOpen={isAnalyticsModalOpen}
        onClose={handleCloseModals}
      />

      {/* Edit Template Modal */}
      <TemplateFormModal
        isOpen={isEditModalOpen}
        onClose={handleCloseModals}
        onSubmit={handleUpdateTemplate}
        title="Edit Template"
        formData={templateForm}
        validationErrors={validationErrors}
        formSubmitting={formSubmitting}
        onFormChange={handleFormChange}
        onVariableChange={handleVariableChange}
        onAddVariable={addVariable}
        onRemoveVariable={removeVariable}
        onAddCtaButton={addCtaButton}
        onRemoveCtaButton={removeCtaButton}
        onCtaButtonChange={handleCtaButtonChange}
      />
    </div>
  );
};

export default ExternalTemplatesPage;
